import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Col, Modal, Result, Row, Spin, Typography } from "antd";
import { InputCurrency } from "components";
import { formatMoney } from "utils";
import { createInternalTransfer } from "services/internal-transfer-service";
import { CompanyBalance, InternalTransferCompanySelect } from "./internal-transfer-company-select";
import { useCompanyBalance, validateTransfer } from "./internal-transfer-balance";
import { CheckCircleFilled, CloseCircleFilled, ExclamationCircleFilled, LoadingOutlined } from "@ant-design/icons";

export const SEND = "send";
export const RECEIVE = "receive";
export const CONFIRM = "confirm";

const FORM = "form";
const LOADING = "loading";
const SUCCESS = "success";
const ERROR = "error";
const UNCERTAIN = "uncertain";

const TITLES = {
    [SEND]: "Enviar transferência",
    [RECEIVE]: "Receber transferência",
    [CONFIRM]: "Confirmar transferência"
};

const UNCERTAIN_MESSAGE = "Não conseguimos confirmar a transferência agora. Ela será verificada automaticamente; confira o relatório em alguns minutos.";

const resultOf = (res) => {
    const code = res.status && res.status.code;
    if (res.httpStatus === 200 && code === "C") {
        return { step: SUCCESS };
    }
    if (code === "F") {
        return { step: ERROR, message: res.errorMessage || res.message };
    }
    if (res.httpStatus === 202 || !res.httpStatus || res.httpStatus >= 500) {
        return { step: UNCERTAIN, message: UNCERTAIN_MESSAGE };
    }
    return { step: ERROR, message: res.errorMessage };
};

export const InternalTransferModal = (props) => {
    const { open, mode, company, companies = [], transfer, onClose } = props;
    const requestKey = useRef(null);
    const submitting = useRef(false);
    const [step, setStep] = useState(FORM);
    const [message, setMessage] = useState(null);
    const [validation, setValidation] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [amount, setAmount] = useState(0);
    const [fixedBalance, loadFixedBalance] = useCompanyBalance();
    const [selectedBalance, loadSelectedBalance] = useCompanyBalance();
    const sending = mode === SEND;
    const companyId = company && company.id;

    useEffect(() => {
        if (open) {
            requestKey.current = crypto.randomUUID();
            submitting.current = false;
            setStep(FORM);
            setMessage(null);
            setValidation(null);
            setSelectedId(null);
            setAmount(0);
            loadSelectedBalance(null);
            loadFixedBalance(mode === CONFIRM ? null : companyId);
        }
    }, [open, mode, companyId, loadFixedBalance, loadSelectedBalance]);

    const onSelect = (id) => {
        setSelectedId(id);
        setValidation(null);
        loadSelectedBalance(id);
    };

    const buildBody = () => {
        if (mode === CONFIRM) {
            return { fromCompany: transfer.from.id, toCompany: transfer.to.id, amount: transfer.amount };
        }
        return sending
            ? { fromCompany: company.id, toCompany: selectedId, amount }
            : { fromCompany: selectedId, toCompany: company.id, amount };
    };

    const submit = async () => {
        if (step !== FORM || submitting.current) {
            return;
        }
        const body = buildBody();
        if (mode !== CONFIRM) {
            const error = validateTransfer({
                fromId: body.fromCompany,
                toId: body.toCompany,
                amount: body.amount,
                fromBalance: sending ? fixedBalance : selectedBalance
            });
            if (error) {
                setValidation(error);
                return;
            }
        }
        submitting.current = true;
        setStep(LOADING);
        const res = await createInternalTransfer({ requestKey: requestKey.current, ...body });
        const result = resultOf(res);
        setMessage(result.message);
        setStep(result.step);
    };

    const close = () => {
        if (step === LOADING) {
            return;
        }
        onClose(step !== FORM);
    };

    const renderForm = () => {
        if (mode === CONFIRM) {
            return (
                <>
                    <Typography.Title level={4}>{`Você está transferindo ${formatMoney(transfer.amount)}`}</Typography.Title>
                    <Typography.Paragraph>{`Origem: ${transfer.from.fantasyName}`}</Typography.Paragraph>
                    <Typography.Paragraph>{`Destino: ${transfer.to.fantasyName}`}</Typography.Paragraph>
                </>
            );
        }
        return (
            <Row gutter={[0, 16]}>
                <Col span={24}>
                    <Typography.Text strong>{sending ? "Origem" : "Destino"}</Typography.Text>
                    <div>{company && company.fantasyName}</div>
                    <CompanyBalance balance={fixedBalance} />
                </Col>
                <Col span={24}>
                    <Typography.Text strong>{sending ? "Destino" : "Origem"}</Typography.Text>
                    <InternalTransferCompanySelect
                        companies={companies}
                        excludeId={company && company.id}
                        value={selectedId}
                        onChange={onSelect}
                        balance={selectedBalance}
                        placeholder="Selecione uma empresa"
                    />
                </Col>
                <Col span={24}>
                    <Typography.Text strong>Valor</Typography.Text>
                    <InputCurrency value={amount} onChange={(value) => { setAmount(value); setValidation(null); }} />
                </Col>
                {validation && (
                    <Col span={24}>
                        <Alert type="warning" showIcon message={validation} />
                    </Col>
                )}
            </Row>
        );
    };

    const renderStep = () => {
        switch (step) {
            case LOADING:
                return <Result icon={<Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />} title="Aguarde, estamos transferindo…" />;
            case SUCCESS:
                return <Result icon={<CheckCircleFilled style={{ fontSize: 48, color: "#52c41a" }} />} status="success" title="Transferência realizada com sucesso!" />;
            case ERROR:
                return <Result icon={<CloseCircleFilled style={{ fontSize: 48, color: "#ff4d4f" }} />} status="error" title="Não foi possível realizar a transferência" subTitle={message} />;
            case UNCERTAIN:
                return <Result icon={<ExclamationCircleFilled style={{ fontSize: 48, color: "#faad14" }} />} status="warning" title="Transferência em verificação" subTitle={message} />;
            default:
                return renderForm();
        }
    };

    const footer = step === FORM
        ? [
            <Button key="cancel" onClick={close}>Cancelar</Button>,
            <Button key="submit" type="primary" onClick={submit}>Confirmar transferência</Button>
        ]
        : step === LOADING
            ? null
            : [<Button key="close" type="primary" onClick={close}>Fechar</Button>];

    return (
        <Modal
            open={open}
            title={step === FORM ? TITLES[mode] : null}
            footer={footer}
            closable={step !== LOADING}
            maskClosable={false}
            keyboard={step !== LOADING}
            onCancel={close}
            destroyOnClose
        >
            {renderStep()}
        </Modal>
    );
};
